// sync-inspector.util.ts — the inspector panel for one finding: every signal we
// hold for that span, read off the same clock, plus a one-line verdict naming
// which mechanism dropped the speech. Pure and spec'd.

import type { Finding, SyncMark } from './sync-findings.util';
import { mmss, type TranscriptModel } from './transcript-timing.util';

export interface InspectorRow {
  label: string;
  value: string;
  tone: 'plain' | 'ok' | 'bad' | 'dim';
}

export interface Inspection {
  title: string;
  rows: InspectorRow[];
  verdict: string;
  bad: boolean;
}

export interface LegEnvelopes { hz: number; human: Uint8Array; agent: Uint8Array; }

/** Below this peak (of 255) a leg is flat: nothing reached the recording. */
const FLAT_PEAK = 20;
/** The envelope's full scale is RMS 0.25 (see audio_envelope.py). */
const FULL_SCALE_RMS = 0.25;
/** Text for a span may arrive this long after it (finalize latency). */
const TEXT_GRACE_SEC = 8;

export function inspectSpan(f: Finding, model: TranscriptModel, marks: SyncMark[], env: LegEnvelopes): Inspection {
  const within = (m: SyncMark, pad = 0) => (m.t1 ?? m.t) >= f.t0 - pad && m.t <= f.t1 + pad;
  const short = (kind: string) => kind.replace(/^interrupt\./, '');
  const human = legRow(env.human, env.hz, f);
  const ai = model.lanes.filter((l) => l.ai).flatMap((l) => l.segs).find((g) => g.at < f.t1 && g.end > f.t0);
  const gate = marks.find((m) => m.kind === 'gate' && m.t1 != null && within(m));
  const interrupts = marks.filter((m) => m.kind.startsWith('interrupt.') && within(m, 0.5));
  const gemini = marks.filter((m) => m.kind === 'gemini_text' && m.t >= f.t0 && m.t <= f.t1 + TEXT_GRACE_SEC);
  const sends = marks.filter((m) => m.kind === 'stt_send' && m.t1 != null && within(m));
  const texts = marks.filter((m) => m.kind === 'stt_text' && m.t >= f.t0 && m.t <= f.t1 + TEXT_GRACE_SEC);
  const guests = marks.filter((m) => m.kind.startsWith('guest:') && m.t1 != null && within(m));

  const rows: InspectorRow[] = [
    human,
    ai ? { label: 'AI leg', value: `speaking (${mmss(ai.at)} – ${mmss(ai.end)})`, tone: 'plain' }
      : { label: 'AI leg', value: 'silent', tone: 'dim' },
    gate ? { label: 'Echo gate', value: `closed ${mmss(gate.t)} – ${mmss(gate.t1!)}`, tone: 'bad' }
      : { label: 'Echo gate', value: 'open', tone: 'ok' },
    interrupts.length
      ? { label: 'Interrupt', value: interrupts.map((m) => `${short(m.kind)} at ${mmss(m.t)}`).join(', '),
        tone: interrupts.some((m) => /_flush$|_honored$/.test(m.kind)) ? 'ok' : 'bad' }
      : { label: 'Interrupt', value: 'none', tone: 'dim' },
    gemini.length ? { label: 'Gemini input text', value: `${gemini.length} line(s), first at ${mmss(gemini[0].t)}`, tone: 'ok' }
      : { label: 'Gemini input text', value: 'none', tone: 'dim' },
    sttRow(sends, texts),
    guests.length ? { label: 'Guest chunks', value: `arriving ${mmss(guests[0].t)} – ${mmss(guests[guests.length - 1].t1!)}`, tone: 'ok' }
      : { label: 'Guest chunks', value: 'n/a (host mic)', tone: 'dim' },
  ];

  const verdict = verdictFor(f, { flat: human.tone === 'bad', gated: !!gate && gate.t <= f.t0,
    sent: sends.length > 0, text: texts.length + gemini.length > 0, interrupts: interrupts.map((m) => short(m.kind)) });
  return { title: `Span ${mmss(f.t0)} – ${mmss(f.t1)}`, rows, verdict, bad: f.kind !== 'late' };
}

function legRow(levels: Uint8Array, hz: number, f: Finding): InspectorRow {
  if (!levels.length) return { label: 'Human leg', value: 'no waveform (older recording)', tone: 'dim' };
  let peak = 0;
  for (let i = Math.floor(f.t0 * hz); i < Math.ceil(f.t1 * hz) && i < levels.length; i++) peak = Math.max(peak, levels[i]);
  if (peak < FLAT_PEAK) return { label: 'Human leg', value: 'flat (nothing reached the recording)', tone: 'bad' };
  const dbfs = Math.round(20 * Math.log10((peak / 255) * FULL_SCALE_RMS));
  return { label: 'Human leg', value: `speech, peak ${dbfs} dBFS`, tone: 'ok' };
}

function sttRow(sends: SyncMark[], texts: SyncMark[]): InspectorRow {
  if (sends.length) {
    const span = `audio sent ${mmss(sends[0].t)} – ${mmss(sends[sends.length - 1].t1!)}`;
    return texts.length ? { label: 'Windowed STT', value: `${span}, ${texts.length} text`, tone: 'ok' }
      : { label: 'Windowed STT', value: `${span}, no text back`, tone: 'bad' };
  }
  if (texts.length) return { label: 'Windowed STT', value: `text at ${mmss(texts[0].t)}`, tone: 'ok' };
  return { label: 'Windowed STT', value: 'no window sent', tone: 'dim' };
}

function verdictFor(f: Finding, s: { flat: boolean; gated: boolean; sent: boolean; text: boolean; interrupts: string[] }): string {
  switch (f.kind) {
    case 'phantom': return 'text with no speech under it → an echo was transcribed';
    case 'late': return 'the text arrived late, it is not missing';
    case 'talked_over':
      return s.interrupts.length
        ? `interrupt not honored (${s.interrupts.join(', ')}) → the AI kept speaking`
        : 'no interrupt was attempted → the onset never reached the model';
    default:
      if (s.gated || f.kind === 'gated_onset') return 'gated onset → never heard → never transcribed';
      if (s.flat) return 'audio never reached the recording → relay / transport problem';
      if (s.sent && !s.text) return 'heard and sent to STT, but no text came back';
      if (!s.text) return 'heard, but neither Gemini nor the windowed STT produced text';
      return 'text exists but was not attributed to this span';
  }
}
